// src/pages/home/PlayerRegister.jsx
import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import API from "../../api/api";
import Navbar from "../../components/Navbar";
import { validateAadhaar } from "../../components/common/AadhaarValidation";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import SearchableSelect from "../../components/common/SearchableSelect";
import { FaSpinner } from "react-icons/fa";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OTP_DEFAULT_RESEND_SECONDS = 120;

const formatCountdown = (seconds) => {
  const total = Math.max(0, Number(seconds || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const FileTile = ({ preview }) => {
  if (!preview) return <div className="text-xs text-slate-400">No file</div>;
  return preview.type === 'image' ? (
    <img src={preview.url} alt="Preview" className="w-full h-20 object-cover rounded-lg" />
  ) : (
    <div className="w-full h-20 bg-red-100 rounded-lg flex items-center justify-center text-xs font-semibold text-red-700">{preview.type}</div>
  );
};

FileTile.propTypes = {
  preview: PropTypes.shape({
    type: PropTypes.string,
    url: PropTypes.string,
  }),
};

const FormSection = ({ title, number, children }) => (
  <div className="border border-slate-200 rounded-xl p-6">
    <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-3">
      <span className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-bold">{number}</span>
      {title}
    </h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
      {children}
    </div>
  </div>
);

FormSection.propTypes = {
  title: PropTypes.string.isRequired,
  number: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
};

const Input = React.forwardRef(({ label, name, error, className, ...props }, ref) => (
  <div className={className}>
    <label htmlFor={name} className="block text-sm font-medium text-slate-700 mb-1">
      {label}
    </label>
    <input
      id={name}
      name={name}
      ref={ref}
      className={`w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none transition-shadow ${error ? 'border-red-500 ring-1 ring-red-500' : ''}`}
      {...props}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
));

const FileInput = ({ label, name, icon, preview, onClear, ...props }) => (
  <div className="text-center">
    <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
    <div className="relative w-full h-32 bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
      {preview ? (
        <>
          {preview.type === 'image' ? (
            <img src={preview.url} alt="Preview" className="w-full h-full object-cover rounded-lg" />
          ) : (
            <div className="text-center p-2">
              <span className="text-4xl">📄</span>
              <p className="text-xs text-slate-600 break-all">{preview.name}</p>
            </div>
          )}
          <button type="button" onClick={onClear} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs shadow-lg">&times;</button>
        </>
      ) : (
        <div className="text-slate-400">{icon}</div>
      )}
    </div>
    <label className="w-full mt-2 inline-block py-2 px-3 rounded-lg font-semibold text-sm bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer transition-colors">
      <input type="file" name={name} className="hidden" {...props} />
      {preview ? 'Change File' : `Upload ${label}`}
    </label>
  </div>
);


const PlayerRegister = () => {
  const { setToken, setRole, setUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ name: "", relation: "", dob: "", gender: "", mobile: "", aadhaar_number: "", email: "", email_otp: "", password: "", district: "", mandal: "", postoffice: "", club: "", coach: "", address: "", state: "Andhra Pradesh", pincode: "", photo: null, aadhaar: null, birth_certificate: null, sadarem: null, others: null });
  const [loading, setLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);
  const [otpNotice, setOtpNotice] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [previews, setPreviews] = useState({});
  const [errors, setErrors] = useState({ email: "", email_otp: "", aadhaar_number: "" });
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [selectedPostOffice, setSelectedPostOffice] = useState("");

  const emailRef = useRef(null);
  const aadhaarRef = useRef(null);
  const otpVerifyRequestIdRef = useRef(0);
  const otpLastVerifyKeyRef = useRef("");
  
  // --- Effects ---

  // File previews
  useEffect(() => {
    const newPreviews = {};
    const toRevoke = [];
    Object.keys(formData).forEach(key => {
      const file = formData[key];
      if (file instanceof File) {
        newPreviews[key] = { name: file.name, type: file.type.startsWith('image/') ? 'image' : 'file', url: URL.createObjectURL(file) };
        toRevoke.push(newPreviews[key].url);
      }
    });
    setPreviews(newPreviews);
    return () => toRevoke.forEach(URL.revokeObjectURL);
  }, [formData]);

  // Pincode lookup
  useEffect(() => {
    const pincode = String(formData.pincode || "").trim();
    if (pincode.length !== 6) {
      setPincodeOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const rows = await API.lookupPincode(pincode);
        setPincodeOptions(Array.isArray(rows) ? rows : []);
        if (rows.length === 1) {
          const one = rows[0];
          setSelectedPostOffice(one.postoffice || "");
          setFormData(prev => ({ ...prev, postoffice: one.postoffice || "", mandal: one.mandal || "", district: one.district || "", state: one.state || "Andhra Pradesh"}));
        }
      } catch (err) {
        setPincodeOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formData.pincode]);

  useEffect(() => {
    const email = String(formData.email || "").trim();
    const otp = String(formData.email_otp || "").trim();

    if (!emailRegex.test(email) || otp.length !== 6 || otpVerified) {
      if (otp.length !== 6) {
        otpLastVerifyKeyRef.current = "";
      }
      if (!otpVerified) {
        otpVerifyRequestIdRef.current += 1;
        setOtpVerifying(false);
      }
      return;
    }

    const verifyKey = `${email.toLowerCase()}|${otp}`;
    if (otpLastVerifyKeyRef.current === verifyKey) {
      return;
    }
    otpLastVerifyKeyRef.current = verifyKey;

    const requestId = ++otpVerifyRequestIdRef.current;
    setOtpVerifying(true);
    setOtpVerified(false);

    (async () => {
      try {
        await API.verifyPlayerRegistrationOtp(email, otp);
        if (otpVerifyRequestIdRef.current !== requestId) return;

        setOtpVerified(true);
        setOtpResendCooldown(0);
        setErrors(prev => ({ ...prev, email_otp: "" }));
        setOtpNotice("OTP verified successfully. You can continue registration.");
      } catch (err) {
        if (otpVerifyRequestIdRef.current !== requestId) return;

        if ((err?.status || 0) >= 500 || err?.code === "NO_RESPONSE") {
          otpLastVerifyKeyRef.current = "";
        }
        setOtpVerified(false);
        const message = err?.message || "Invalid OTP. Please resend OTP and try again.";
        setErrors(prev => ({ ...prev, email_otp: message }));
        setOtpNotice("OTP verification failed. Please resend OTP.");
      } finally {
        if (otpVerifyRequestIdRef.current === requestId) {
          setOtpVerifying(false);
        }
      }
    })();
  }, [formData.email, formData.email_otp, otpVerified]);

  useEffect(() => {
    if (otpResendCooldown <= 0 || otpVerified) return;
    const timer = setInterval(() => {
      setOtpResendCooldown(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpResendCooldown, otpVerified]);

  // --- Handlers ---
  
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      if (name === "aadhaar_number") {
        const cleaned = String(value || "").replace(/\D/g, "").slice(0, 12);
        setFormData(prev => ({ ...prev, aadhaar_number: cleaned }));
        setErrors(e => ({ ...e, aadhaar_number: "" }));
        return;
      }
      if (name === "email_otp") {
        const cleaned = String(value || "").replace(/\D/g, "").slice(0, 6);
        setFormData(prev => ({ ...prev, email_otp: cleaned }));
        setOtpVerified(false);
        setErrors(e => ({ ...e, email_otp: "" }));
        return;
      }
      if (name === "email") {
        setFormData(prev => ({ ...prev, email: value, email_otp: "" }));
        setOtpVerified(false);
        setOtpSent(false);
        setOtpResendCooldown(0);
        otpLastVerifyKeyRef.current = "";
        setErrors(e => ({ ...e, email: "", email_otp: "" }));
        setOtpNotice("");
        return;
      }
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handlePincodeChange = (e) => {
    const clean = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFormData(prev => ({ ...prev, pincode: clean, mandal: "", district: "", postoffice: "" }));
    setSelectedPostOffice("");
  }
  
  const handlePostOfficeChange = (e) => {
    const val = e.target.value;
    setSelectedPostOffice(val);
    const selected = pincodeOptions.find(opt => opt.postoffice === val);
    if(selected) {
        setFormData(prev => ({ ...prev, postoffice: val, mandal: selected.mandal, district: selected.district, state: selected.state }));
    }
  };
  
  const handleClearFile = (name) => {
    setFormData(prev => ({ ...prev, [name]: null }));
  };

  const checkEmailAvailability = async (rawEmail) => {
    const email = String(rawEmail || "").trim();
    if (!email || !emailRegex.test(email)) return { ok: false };
    try {
      const res = await API.get("/auth/check-email", { params: { email } });
      if (res?.exists) {
        setErrors(prev => ({ ...prev, email: "Email already registered" }));
        return { ok: false };
      }
      return { ok: true };
    } catch (_err) {
      return { ok: true };
    }
  };

  const checkAadhaarAvailability = async (rawAadhaar) => {
    const aadhaar = String(rawAadhaar || "").trim();
    if (!aadhaar || aadhaar.length !== 12 || !validateAadhaar(aadhaar)) return { ok: false };
    try {
      const res = await API.get("/auth/check-aadhaar", { params: { aadhaar } });
      if (res?.invalid) {
        setErrors(prev => ({ ...prev, aadhaar_number: "Invalid Aadhaar number" }));
        return { ok: false };
      }
      if (res?.exists) {
        setErrors(prev => ({ ...prev, aadhaar_number: "Aadhaar number already registered" }));
        return { ok: false };
      }
      return { ok: true };
    } catch (_err) {
      return { ok: true };
    }
  };

  const handleEmailBlur = async () => {
    const email = String(formData.email || "").trim();
    if (!email) return;
    if (!emailRegex.test(email)) {
      setErrors(prev => ({ ...prev, email: "Invalid email format" }));
      return;
    }
    await checkEmailAvailability(email);
  };

  const handleAadhaarBlur = async () => {
    const aadhaar = String(formData.aadhaar_number || "").trim();
    if (!aadhaar) return;
    if (aadhaar.length !== 12 || !validateAadhaar(aadhaar)) {
      setErrors(prev => ({ ...prev, aadhaar_number: "Invalid Aadhaar number" }));
      return;
    }
    await checkAadhaarAvailability(aadhaar);
  };

  const handleSendEmailOtp = async () => {
    const email = String(formData.email || "").trim();
    if (otpVerified) return;

    if (!emailRegex.test(email)) {
      setErrors(prev => ({ ...prev, email: "Enter a valid email before requesting OTP" }));
      emailRef.current?.focus();
      return;
    }

    const emailCheck = await checkEmailAvailability(email);
    if (!emailCheck.ok) {
      emailRef.current?.focus();
      return;
    }

    setOtpSending(true);
    setOtpVerified(false);

    try {
      const res = await API.sendPlayerRegistrationOtp(email);
      const expiryMinutes = Math.max(1, Math.round(Number(res?.data?.expires_in || 600) / 60));
      const resendAfter = Math.max(
        0,
        Number(res?.data?.resend_after || OTP_DEFAULT_RESEND_SECONDS)
      );

      setOtpSent(true);
      setOtpResendCooldown(resendAfter);
      otpLastVerifyKeyRef.current = "";
      setOtpNotice(`OTP sent to ${email}. It stays valid for about ${expiryMinutes} minutes.`);
      setFormData(prev => ({ ...prev, email_otp: "" }));
      setErrors(prev => ({ ...prev, email_otp: "" }));

      await Swal.fire({
        icon: "success",
        title: "OTP Sent",
        text: `We sent a 6-digit OTP to ${email}.`,
      });
    } catch (err) {
      const message = err?.message || "Please try again in a moment.";
      const retryMatch = String(message).match(/(\d+)\s*seconds?/i);
      if (retryMatch) {
        const retryAfter = Number(retryMatch[1] || 0);
        setOtpSent(true);
        setOtpResendCooldown(retryAfter);
        setOtpNotice(`Please wait ${formatCountdown(retryAfter)} before resending OTP.`);
      } else {
        setOtpNotice("");
      }
      await Swal.fire({
        icon: "error",
        title: "Unable to Send OTP",
        text: message,
      });
    } finally {
      setOtpSending(false);
    }
  };

  const validateForm = async () => {
    const required = ["name", "relation", "dob", "gender", "mobile", "aadhaar_number", "email", "email_otp", "password", "district", "mandal", "address", "state", "pincode"];
    const missing = required.filter(k => !formData[k]?.toString().trim());
    if (missing.length > 0) {
      Swal.fire({ icon: 'warning', title: 'Missing Fields', text: `Please fill out: ${missing.join(", ")}` });
      return false;
    }
    if (!emailRegex.test(formData.email)) {
      setErrors(e => ({ ...e, email: "Invalid email format" }));
      return false;
    }
    if (!validateAadhaar(formData.aadhaar_number)) {
      setErrors(e => ({ ...e, aadhaar_number: "Invalid Aadhaar number" }));
      return false;
    }
    if (!/^\d{6}$/.test(String(formData.email_otp || ""))) {
      setErrors(e => ({ ...e, email_otp: "Enter the 6-digit OTP sent to your email" }));
      return false;
    }
    if (!otpVerified) {
      setErrors(e => ({ ...e, email_otp: "Please enter a valid OTP and wait for verification." }));
      await Swal.fire({
        icon: "warning",
        title: "OTP Not Verified",
        text: "Enter the 6-digit OTP and wait for verification before proceeding.",
      });
      return false;
    }
    const [emailCheck, aadhaarCheck] = await Promise.all([
      checkEmailAvailability(formData.email),
      checkAadhaarAvailability(formData.aadhaar_number),
    ]);
    if (!emailCheck.ok || !aadhaarCheck.ok) {
      return false;
    }
    return true;
  };

  const handleOpenPreview = async (e) => {
    e.preventDefault();
    if (await validateForm()) {
      setShowPreview(true);
    }
  };

  const submitRegistration = async () => {
    if (!(await validateForm())) return;
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(formData).forEach(([k, v]) => v && fd.append(k, v));

      const res = await API.registerPlayer(fd);
      const { role = "player", user } = res;
      
      // Use AuthContext to set auth state
      setUser(user);
      setToken("cookie-auth");
      setRole(role);

      await Swal.fire({ icon: 'success', title: 'Registration Successful!', text: 'Welcome to  Para Sports Accociation of Andhra Pradesh!', timer: 2000, showConfirmButton: false });
      navigate('/player-profile'); // Redirect to a relevant page
    } catch (err) {
      const serverErrors = err?.errors && typeof err.errors === "object" ? err.errors : {};
      if (serverErrors.email?.[0]) {
        setErrors(prev => ({ ...prev, email: serverErrors.email[0] }));
      }
      if (serverErrors.email_otp?.[0]) {
        setErrors(prev => ({ ...prev, email_otp: serverErrors.email_otp[0] }));
        setOtpVerified(false);
      }
      if (serverErrors.aadhaar_number?.[0]) {
        setErrors(prev => ({ ...prev, aadhaar_number: serverErrors.aadhaar_number[0] }));
      }
      const firstValidationMessage = Object.values(serverErrors).flat()[0];
      const message = err?.message || firstValidationMessage || "An unexpected error occurred.";
      Swal.fire({ icon: 'error', title: 'Registration Failed', text: message });
    } finally {
      setLoading(false);
      setShowPreview(false);
    }
  };
  
  // --- Derived State ---
  const mandalOptions = useMemo(() => [...new Set(pincodeOptions.map(opt => opt.mandal).filter(Boolean))], [pincodeOptions]);
  const districtOptions = useMemo(() => [...new Set(pincodeOptions.map(opt => opt.district).filter(Boolean))], [pincodeOptions]);
  const canPreviewRegister = otpVerified && !otpVerifying && !loading;
  const otpResendLocked = otpSent && otpResendCooldown > 0;
  const otpButtonText = otpSending
    ? "Sending..."
    : otpResendLocked
      ? `Resend in ${formatCountdown(otpResendCooldown)}`
      : otpSent
        ? "Resend OTP"
        : "Send OTP";

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="flex-grow flex items-center justify-center p-4">
        <div className="w-full max-w-6xl mx-auto bg-white shadow-xl rounded-2xl grid md:grid-cols-5 overflow-hidden">
          
          <div className="hidden md:flex md:col-span-2 flex-col items-center justify-center p-12 bg-blue-600 text-white text-center">
            <img src="/images/parasports.png" alt="Parasports Logo" className="w-24 h-auto mb-6" />
            <h1 className="text-3xl font-bold mb-2">Join the Movement</h1>
            <p className="text-blue-100">Become a part of Andhra Pradesh's proud para sports legacy.</p>
          </div>

          <div className="md:col-span-3 p-8 sm:p-12 overflow-y-auto" style={{maxHeight: 'calc(100vh - 150px)'}}>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Player Registration</h2>
            <p className="text-slate-600 mb-8">Create your athlete profile to get started.</p>
            
            <form onSubmit={handleOpenPreview} className="space-y-8">
              <FormSection title="Personal Information" number={1}>
                <Input label="Full Name *" name="name" value={formData.name} onChange={handleChange} required />
                <Input label="Father/Husband Name *" name="relation" value={formData.relation} onChange={handleChange} required />
                <Input label="Date of Birth *" name="dob" type="date" value={formData.dob} onChange={handleChange} required />
                <div>
                   <label htmlFor="gender" className="block text-sm font-medium text-slate-700 mb-1">Gender *</label>
                   <select id="gender" name="gender" value={formData.gender} onChange={handleChange} required className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none">
                       <option value="">Select Gender</option>
                       <option value="Male">Male</option>
                       <option value="Female">Female</option>
                       <option value="Other">Other</option>
                   </select>
                </div>
                <Input label="Mobile Number *" name="mobile" type="tel" value={formData.mobile} onChange={handleChange} required />
                <Input ref={aadhaarRef} label="Aadhaar Number *" name="aadhaar_number" value={formData.aadhaar_number} onChange={handleChange} onBlur={handleAadhaarBlur} maxLength="12" error={errors.aadhaar_number} required />
                <Input ref={emailRef} label="Email *" name="email" type="email" value={formData.email} onChange={handleChange} onBlur={handleEmailBlur} error={errors.email} required />
                <div className="sm:col-span-2 rounded-xl border border-blue-200 bg-blue-50/60 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-end">
                    <Input
                      label="Email OTP *"
                      name="email_otp"
                      value={formData.email_otp}
                      onChange={handleChange}
                      maxLength="6"
                      inputMode="numeric"
                      error={errors.email_otp}
                      className="flex-1"
                      required
                      disabled={otpVerified}
                    />
                    {!otpVerified && (
                      <button
                        type="button"
                        onClick={handleSendEmailOtp}
                        disabled={otpSending || otpVerifying || otpResendLocked || !String(formData.email || "").trim()}
                        className="w-full md:w-auto whitespace-nowrap rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                      >
                        {otpButtonText}
                      </button>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-blue-900">
                    {otpVerifying
                      ? "Verifying OTP..."
                      : otpVerified
                        ? "OTP verified. You can proceed."
                        : otpNotice || "We’ll verify your email with a one-time password before creating the account."}
                  </p>
                  {!otpVerified && otpResendLocked && (
                    <p className="mt-1 text-xs text-blue-700">
                      You can request OTP again in {formatCountdown(otpResendCooldown)}.
                    </p>
                  )}
                </div>
                <Input label="Create Password *" name="password" type="password" value={formData.password} onChange={handleChange} required minLength="8" />
              </FormSection>

              <FormSection title="Location & Contact" number={2}>
                  <Input label="Pincode *" name="pincode" value={formData.pincode} onChange={handlePincodeChange} required />
                  <div>
                      <label htmlFor="postoffice" className="block text-sm font-medium text-slate-700 mb-1">Village / Post Office</label>
                      <SearchableSelect id="postoffice" name="postoffice" value={selectedPostOffice} onChange={handlePostOfficeChange} disabled={!pincodeOptions.length} className="w-full px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none">
                          <option value="">{pincodeOptions.length ? "Select post office" : "Enter valid pincode"}</option>
                          {pincodeOptions.map((opt, i) => <option key={`${opt.postoffice}-${i}`} value={opt.postoffice}>{opt.postoffice}</option>)}
                      </SearchableSelect>
                  </div>
                  <Input label="Mandal *" name="mandal" list="mandal-opts" value={formData.mandal} onChange={handleChange} required /><datalist id="mandal-opts">{mandalOptions.map(o => <option key={o} value={o}/>)}</datalist>
                  <Input label="District *" name="district" list="district-opts" value={formData.district} onChange={handleChange} required /><datalist id="district-opts">{districtOptions.map(o => <option key={o} value={o}/>)}</datalist>
                  <Input label="State *" name="state" value={formData.state} onChange={handleChange} required />
                  <Input label="Full Address *" name="address" value={formData.address} onChange={handleChange} required className="sm:col-span-2"/>
              </FormSection>

              <FormSection title="Sports Information" number={3}>
                <Input label="Club / Academy" name="club" value={formData.club} onChange={handleChange} />
                <Input label="Coach Name" name="coach" value={formData.coach} onChange={handleChange} />
              </FormSection>
              
              <FormSection title="Document Uploads" number={4}>
                  <div className="sm:col-span-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    <FileInput label="Photo" name="photo" icon="🧍" preview={previews.photo} onClear={() => handleClearFile('photo')} onChange={handleChange} accept="image/*"/>
                    <FileInput label="Aadhaar" name="aadhaar" icon="🆔" preview={previews.aadhaar} onClear={() => handleClearFile('aadhaar')} onChange={handleChange} accept=".jpg,.jpeg,.png,.pdf"/>
                    <FileInput label="Birth Certificate" name="birth_certificate" icon="🎂" preview={previews.birth_certificate} onClear={() => handleClearFile('birth_certificate')} onChange={handleChange} accept=".jpg,.jpeg,.png,.pdf"/>
                    <FileInput label="SADAREM" name="sadarem" icon="📋" preview={previews.sadarem} onClear={() => handleClearFile('sadarem')} onChange={handleChange} accept=".jpg,.jpeg,.png,.pdf"/>
                    <FileInput label="Others" name="others" icon="📎" preview={previews.others} onClear={() => handleClearFile('others')} onChange={handleChange} accept=".jpg,.jpeg,.png,.pdf"/>
                  </div>
              </FormSection>

              <div className="sm:col-span-2 flex justify-end">
                <button type="submit" disabled={!canPreviewRegister} className="w-full sm:w-auto flex justify-center items-center gap-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-bold py-3 px-8 rounded-lg text-lg shadow-md hover:shadow-lg transition-all">
                  {loading && <FaSpinner className="animate-spin" />}
                  {loading ? "Processing..." : otpVerifying ? "Verifying OTP..." : otpVerified ? "Preview & Register" : "Verify OTP to Continue"}
                </button>
              </div>
            </form>
            <p className="text-sm text-center text-slate-600 mt-6">
                Already have an account?{" "}
                <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline">
                  Sign In
                </Link>
              </p>
          </div>
        </div>
      </div>
      
      {showPreview && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                  <div className="flex items-center justify-between p-4 border-b">
                      <h3 className="text-xl font-semibold text-slate-800">Confirm Your Details</h3>
                      <button type="button" onClick={() => setShowPreview(false)} className="text-slate-500 hover:text-slate-800">&times;</button>
                  </div>
                  <div className="p-6 overflow-y-auto space-y-6">
                    {/* Simplified preview */}
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                        {Object.entries(formData)
                          .filter(([k, v]) => !["password", "email_otp"].includes(k) && v && typeof v !== 'object')
                          .map(([k, v]) => (
                          <div key={k} className="flex">
                            <span className="font-semibold text-slate-600 w-32 capitalize">{k.replaceAll('_', ' ')}:</span>
                            <span className="text-slate-800">{v}</span>
                          </div>
                        ))}
                     </div>
                     <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                        {Object.keys(previews).map(k => (
                          <div key={k}><span className="text-sm font-semibold capitalize">{k}:</span><FileTile preview={previews[k]} /></div>
                        ))}
                     </div>
                  </div>
                  <div className="flex justify-end gap-4 p-4 border-t bg-slate-50 rounded-b-lg">
                      <button type="button" onClick={() => setShowPreview(false)} className="font-semibold text-slate-700 bg-slate-200 rounded-lg px-5 py-2 hover:bg-slate-300 transition-all">Edit</button>
                      <button type="button" onClick={submitRegistration} disabled={loading} className="flex items-center gap-2 font-semibold text-white bg-blue-600 rounded-lg px-5 py-2 hover:bg-blue-700 shadow-sm hover:shadow-md transition-all">
                        {loading && <FaSpinner className="animate-spin" />}
                        {loading ? 'Submitting...' : 'Confirm & Submit'}
                      </button>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default PlayerRegister;
